import React, { useState, useEffect } from 'react';
import { Route, Switch, NavLink, Redirect } from 'react-router-dom';
import {
  FaUsers,
  FaClock,
  FaCalendarAlt,
  FaSignOutAlt,
  FaCog,
  FaChartBar,
  FaUser,
  FaAddressBook,
  FaRobot,
  FaBars,
  FaTimes,
} from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LeadsEmbed from './components/LeadsEmbed';
import Usuarios from './components/Usuarios';
import Horarios from './components/Horarios';
import Citas from './components/Citas';
import Servicios from './components/Servicios';
import Leads from './components/Leads';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UserProfile from './components/UserProfile';
import AI from './components/AI';
import Integraciones from './components/Integraciones';
import DeviceApproval from './components/DeviceApproval';
import LandingPublic from './components/LandingPublic';
import AdminPlanesPagos from './components/AdminPlanesPagos';
import axios from 'axios';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

const apiBaseUrl = (process.env.REACT_APP_API_URL || '').trim();

// Configurar axios para incluir el token en todas las peticiones, excepto para /ai y KnowledgeBase
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('shortToken');
    // La siguiente línea ha sido comentada para desactivar la adición automática del token
    // if (token && !config.url.includes('/ai') && !config.url.includes('KnowledgeBase')) {
    //   config.headers['Authorization'] = `Bearer ${token}`;
    // }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

axios.defaults.baseURL = apiBaseUrl;

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navItems = [
    { to: '/', exact: true, label: 'Dashboard', icon: FaChartBar, adminOnly: false },
    { to: '/horarios', exact: false, label: 'Horarios', icon: FaClock, adminOnly: false },
    { to: '/citas', exact: false, label: 'Citas', icon: FaCalendarAlt, adminOnly: false },
    { to: '/servicios', exact: false, label: 'Servicios', icon: FaCog, adminOnly: false },
    { to: '/leads', exact: false, label: 'Leads', icon: FaAddressBook, adminOnly: false },
    { to: '/ai', exact: false, label: 'AI', icon: FaRobot, adminOnly: false },
    { to: '/integraciones', exact: false, label: 'Integraciones', icon: FaCog, adminOnly: false },
  ];

  useEffect(() => {
    const token = localStorage.getItem('shortToken');
    const userIsAdmin = localStorage.getItem('isAdmin') === 'true';
    setIsLoggedIn(!!token);
    setIsAdmin(userIsAdmin);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('shortToken');
    localStorage.removeItem('isAdmin');
    setIsLoggedIn(false);
    setIsAdmin(false);
    window.location.href = '/';
  };

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const pathname = window.location.pathname || '';
  const isPublicDeviceRoute = pathname.startsWith('/device');
  const isPublicEmbedRoute = pathname.startsWith('/nimboxtalk_embed');

  if (!isLoggedIn && !isPublicEmbedRoute && !isPublicDeviceRoute) {
    return (
      <div className="cp-app min-h-screen bg-slate-950">
        <Switch>
          <Route exact path="/" component={LandingPublic} />
          <Route path="/device" component={DeviceApproval} />
          <Route
            path="/login"
            render={(props) => <Login {...props} setIsLoggedIn={setIsLoggedIn} setIsAdmin={setIsAdmin} />}
          />
          <Redirect to="/" />
        </Switch>
      </div>
    );
  }

  return (
    <div className="cp-app flex min-h-screen bg-slate-100">
      <Switch>
        <Route
          path="/nimboxtalk_embed"
          render={(props) => {
            const urlParams = new URLSearchParams(props.location.search);
            const token = urlParams.get('token');
            if (!token) {
              return <Redirect to="/login" />;
            }
            return <LeadsEmbed token={token} />;
          }}
        />
        <Route>
          <>
            {isMobile && (
              <Button
                variant="secondary"
                size="sm"
                className="fixed left-3 top-3 z-50 rounded-full"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
              </Button>
            )}

            <aside
              className={cn(
                'fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-200 bg-white transition duration-200 ease-in-out md:static md:translate-x-0',
                isMenuOpen ? 'translate-x-0' : '-translate-x-full'
              )}
            >
              <div className="flex h-full flex-col">
                <div className="border-b border-slate-200 px-5 py-5">
                  <h1 className="text-2xl font-bold text-blue-600">CitasPro</h1>
                  <p className="mt-1 text-xs text-slate-500">by Nimbox360</p>
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-4">
                  <ul className="space-y-1">
                    {navItems
                      .filter((item) => (item.adminOnly ? isAdmin : true))
                      .map((item) => {
                        const Icon = item.icon;
                        return (
                          <li key={item.to}>
                            <NavLink
                              to={item.to}
                              exact={item.exact}
                              className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                              activeClassName="bg-blue-100 text-blue-700"
                              onClick={() => isMobile && setIsMenuOpen(false)}
                            >
                              <Icon className="mr-3" />
                              {item.label}
                            </NavLink>
                          </li>
                        );
                      })}
                  </ul>
                  {isAdmin ? (
                    <div className="mt-5">
                      <p className="px-3 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Admin</p>
                      <ul className="space-y-1">
                        <li>
                          <NavLink
                            to="/usuarios"
                            exact={false}
                            className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                            activeClassName="bg-blue-100 text-blue-700"
                            onClick={() => isMobile && setIsMenuOpen(false)}
                          >
                            <FaUsers className="mr-3" />
                            Usuarios
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/admin/planes"
                            exact={false}
                            className="flex items-center rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                            activeClassName="bg-blue-100 text-blue-700"
                            onClick={() => isMobile && setIsMenuOpen(false)}
                          >
                            <FaCog className="mr-3" />
                            Planes y Pagos
                          </NavLink>
                        </li>
                      </ul>
                    </div>
                  ) : null}
                </nav>
                <div className="border-t border-slate-200 p-3">
                  <div className="flex items-center gap-2">
                    <NavLink
                      to="/profile"
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <FaUser className="mr-2" />
                      Perfil
                    </NavLink>
                    <Button variant="ghost" className="flex-1 justify-center text-slate-700" onClick={handleLogout}>
                      <FaSignOutAlt className="mr-2" />
                      Salir
                    </Button>
                  </div>
                  <p className="mt-3 text-[11px] text-slate-400">&copy; 2026 CitasPro</p>
                </div>
              </div>
            </aside>

            <main className="min-w-0 flex-1 p-3 md:p-8">
              <Switch>
                <Route exact path="/" component={Dashboard} />
                {isAdmin && <Route path="/usuarios" component={Usuarios} />}
                <Route path="/horarios" component={Horarios} />
                <Route path="/citas" component={Citas} />
                <Route path="/servicios" component={Servicios} />
                {isAdmin && <Route path="/admin/planes" component={AdminPlanesPagos} />}
                <Route path="/profile" component={UserProfile} />
                <Route path="/leads" component={Leads} />
                <Route path="/ai" component={AI} />
                <Route path="/integraciones" component={Integraciones} />
                <Route path="/device" component={DeviceApproval} />
                <Redirect to="/" />
              </Switch>
            </main>
          </>
        </Route>
      </Switch>
      <ToastContainer position="bottom-right" />
    </div>
  );
}

export default App;
